/**
 * Screen 14b (edit) — everything needed to put one programme on sale.
 *
 * Laid out in the order the work actually happens: write the name, set the
 * prices, lay out the hall, publish. The publish button reports the server's
 * refusal verbatim, because "no seat map" is a more useful sentence than
 * "could not publish".
 *
 * All three language slots are always shown, including empty ones. A missing
 * translation should be visible rather than something a volunteer has to
 * remember to look for.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorNote, Note, Shell } from "../../components/Shell";
import { LOCALES, LOCALE_NAMES, type Locale } from "../../i18n";
import { ApiError, api, type AdminProgram } from "../../lib/api";
import { formatDateTime, formatIdr } from "../../lib/format";

export default function AdminProgramEdit({ locale }: { locale: Locale }) {
  const { id = "" } = useParams();
  const programId = Number(id);
  const { t } = useTranslation();

  const [program, setProgram] = useState<AdminProgram | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // Per-locale draft text, seeded from the server once loaded.
  const [texts, setTexts] = useState<Record<string, { name: string; description: string; performers: string }>>({});
  const [tierLabel, setTierLabel] = useState("tier.adult");
  const [tierPrice, setTierPrice] = useState("");
  const [rows, setRows] = useState("A,B,C");
  const [perRow, setPerRow] = useState("5");
  const [seatTier, setSeatTier] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await api.admin.program(programId);
      setProgram(body);
      setTexts(
        Object.fromEntries(
          LOCALES.map((l) => [
            l,
            body.texts[l] ?? { name: "", description: "", performers: "" },
          ]),
        ),
      );
      if (body.tiers.length && seatTier === null) setSeatTier(body.tiers[0].id);
    } catch {
      setError(t("error.generic"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** One place for "do it, report what the server said, reload". */
  async function run(action: () => Promise<unknown>, okMessage?: string) {
    setError(null);
    setSaved(null);
    try {
      await action();
      await load();
      if (okMessage) setSaved(okMessage);
    } catch (err) {
      if (err instanceof ApiError) {
        // The server's refusals here are written for a volunteer to read —
        // "no seat map", "3 live bookings" — so they are shown as-is.
        setError(err.message);
      } else {
        setError(t("error.generic"));
      }
    }
  }

  if (!program) {
    return (
      <Shell area="programs" title={t("admin.edit")}>
        {error ? <ErrorNote>{error}</ErrorNote> : <Note>{t("common.loading")}</Note>}
      </Shell>
    );
  }

  const locked = program.bookings > 0;

  return (
    <Shell area="programs" title={t("admin.edit")}>
      <p>
        <span className="state" data-state={program.status === "PUBLISHED" ? "PAID" : "HELD"}>
          {program.status === "PUBLISHED" ? t("admin.published") : t("admin.draft")}
        </span>{" "}
        <span className="fallback-note">{formatDateTime(program.date_time, locale)} · {program.venue}</span>
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}
      {saved && <p className="fallback-note">{saved}</p>}

      {locked && (
        <div data-area="news" className="notice">
          <p>{t("admin.locked_by_bookings", { count: program.bookings })}</p>
        </div>
      )}

      {/* ---------------------------------------------------- 1. content */}
      <h3 className="section">{t("admin.content")}</h3>
      {LOCALES.map((l) => (
        <details key={l} open={l === locale}>
          <summary>
            {LOCALE_NAMES[l]}
            {!program.texts[l] && ` — ${t("admin.empty")}`}
          </summary>
          <div className="ans stack">
            <input
              className="field"
              lang={l}
              value={texts[l]?.name ?? ""}
              placeholder={t("admin.programme_name")}
              onChange={(e) =>
                setTexts({ ...texts, [l]: { ...texts[l], name: e.target.value } })
              }
            />
            <textarea
              className="field"
              lang={l}
              rows={3}
              value={texts[l]?.description ?? ""}
              placeholder={t("program.about")}
              onChange={(e) =>
                setTexts({ ...texts, [l]: { ...texts[l], description: e.target.value } })
              }
            />
            <input
              className="field"
              lang={l}
              value={texts[l]?.performers ?? ""}
              placeholder={t("program.performers")}
              onChange={(e) =>
                setTexts({ ...texts, [l]: { ...texts[l], performers: e.target.value } })
              }
            />
            <button
              type="button"
              className="btn btn--primary"
              disabled={!texts[l]?.name?.trim()}
              onClick={() =>
                run(
                  () => api.admin.putText(program.id, l, texts[l]),
                  t("admin.saved"),
                )
              }
            >
              {t("common.save")}
            </button>
          </div>
        </details>
      ))}

      {/* ------------------------------------------------------ 2. prices */}
      <h3 className="section">{t("admin.prices")}</h3>
      <ul className="cards">
        {program.tiers.map((tier) => (
          <li key={tier.id} className="card">
            <p>
              <strong>{t(tier.label_key)}</strong>{" "}
              <span className="money">{formatIdr(tier.price_idr)}</span>
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => run(() => api.admin.deleteTier(program.id, tier.id))}
            >
              {t("admin.remove")}
            </button>
          </li>
        ))}
      </ul>
      <div className="stack">
        <select
          className="field"
          value={tierLabel}
          onChange={(e) => setTierLabel(e.target.value)}
          aria-label={t("admin.tier")}
        >
          {["tier.adult", "tier.child", "tier.student", "tier.senior"].map((key) => (
            <option key={key} value={key}>
              {t(key)}
            </option>
          ))}
        </select>
        <input
          className="field"
          inputMode="numeric"
          value={tierPrice}
          placeholder="150000"
          onChange={(e) => setTierPrice(e.target.value.replace(/\D/g, ""))}
        />
        <button
          type="button"
          className="btn"
          disabled={!tierPrice}
          onClick={() =>
            run(async () => {
              await api.admin.addTier(program.id, {
                label_key: tierLabel,
                price_idr: Number(tierPrice),
                sort_order: program.tiers.length + 1,
              });
              setTierPrice("");
            })
          }
        >
          {t("admin.add_tier")}
        </button>
      </div>

      {/* -------------------------------------------------- 3. the hall */}
      {program.seating_mode === "NUMBERED" && (
        <>
          <h3 className="section">{t("admin.seat_map")}</h3>
          <p className="fallback-note">
            {program.seats.total} · {t("seat_state.AVAILABLE")} {program.seats.available} ·{" "}
            {t("seat_state.SOLD")} {program.seats.sold}
          </p>
          <div className="stack">
            <input
              className="field"
              value={rows}
              placeholder="A,B,C"
              onChange={(e) => setRows(e.target.value)}
              aria-label={t("admin.rows")}
            />
            <input
              className="field"
              inputMode="numeric"
              value={perRow}
              onChange={(e) => setPerRow(e.target.value.replace(/\D/g, ""))}
              aria-label={t("admin.seats_per_row")}
            />
            <select
              className="field"
              value={seatTier ?? ""}
              onChange={(e) => setSeatTier(Number(e.target.value))}
              aria-label={t("admin.tier")}
            >
              {program.tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {t(tier.label_key)} — {formatIdr(tier.price_idr)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              disabled={locked || !seatTier || !perRow}
              onClick={() =>
                run(
                  () =>
                    api.admin.layOutSeats(program.id, {
                      rows: rows.split(",").map((r) => r.trim()).filter(Boolean),
                      seats_per_row: Number(perRow),
                      tier_id: seatTier!,
                    }),
                  t("admin.saved"),
                )
              }
            >
              {t("admin.lay_out")}
            </button>
          </div>
        </>
      )}

      {/* -------------------------------------------------- 4. publish */}
      <h3 className="section">{t("admin.publish")}</h3>
      {program.status === "PUBLISHED" ? (
        <button
          type="button"
          className="btn"
          onClick={() => run(() => api.admin.unpublish(program.id), t("admin.saved"))}
        >
          {t("admin.unpublish")}
        </button>
      ) : (
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => run(() => api.admin.publish(program.id), t("admin.saved"))}
        >
          {t("admin.publish")}
        </button>
      )}

      <Link className="btn" to="/admin/programs">
        {t("common.back")}
      </Link>
    </Shell>
  );
}
